import React, { useCallback, useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import classname from 'classnames';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { Url } from '@wix/da-url';
import { ThemeOptions, ThemeOverride } from '@wix/da-react-context/pkg/ThemeContext';
import { BiContextProvidedValue, BiData } from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import BiButton from '@wix/da-bi/pkg/Components/BiButton';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import GiveBadge from '@wix/da-ds/pkg/Icons/16x16/GiveBadge';
import Ellipsis from '@wix/da-ds/pkg/Icons/Ellipsis';
import CloseIcon16 from '@wix/da-ds/pkg/Icons/16x16/Close';
import MltSearch16 from '@wix/da-ds/pkg/Icons/16x16/MltSearch';
import { BiContext } from '@wix/da-bi/pkg/BiLogger.context';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import Check from '@wix/da-ds/pkg/Icons/16x16/Check';
import IconButtonWithBackground from '@wix/da-ds/pkg/Buttons/IconButton/IconButtonWithBackground';
import HoverOnly from '@wix/da-ds/pkg/thumbs/OverlayHelpers/HoverOnly';
import { useOverlayContext } from '../../../hooks/useOverlayContext';
import { useRestrictToLoggedIn } from '../../../../utils/hooks/useRestrictToLoggedIn';
import UserAvatar from '../../../../User/Avatar/UserAvatarWithPopover';
import UserLink from '../../../../User/Link/UserLinkWithTooltip';
import { getDeviationResourceType, getDeviationTitle, getIsPost } from '../../../_helpers';
import { DeviationResourceType } from '../../../../types/deviation';
import ThumbActions from '../../../Helper/ThumbActions';
import { useWatchButton } from '../../../../WatchButton/useWatchButton';
import { useDeviationBlockType } from '../../hooks/useDeviationBlockType';
import { useDeviationAttributes } from '../../../hooks/useDeviationAttributes';
import DeviationLink from '../../../../Duperbrowse/DeviationLink';
import FaveControl from '../FaveControl';
import CommentControl from '../CommentControl';
import { getDeviationOwner } from '../../../../utils/adoptables';
import BasicUserName from '../../../../User/BasicUserName/BasicUserName';
import { useSubscriptionTier } from '../../../../utils/hooks/useSubscriptionTier';
import { useIsCurrentUserDeviationAuthor } from '../../../../utils/hooks/useIsCurrentUserDeviationAuthor';
import { beginDeviationAwardBadgeFlow } from '../../../../coreBadge/redux/actionCreators';
import { AwardBadgeSource } from '../../../../coreBadge/types';
import BoostIcon from '@wix/da-ds/pkg/Icons/16x16/BoostFilled';
import s from './ThumbOverlayHover.scss';
const ThumbOverlayHover = ({ deviation, width, height, children, className, extraActions, onRemove })=>{
    var _deviation_author;
    const { showTitle, showAuthor, showOwner, showCommentControl, showAuthorTooltip, showOwnerTooltip, showFaveControl, showExtraActions, showAuthorName, showOwnerName, showAuthorWatchButton, showOwnerWatchButton, showMlt, showAwardBadge } = useOverlayContext(deviation);
    const { t } = useTranslation();
    const isAuthor = useIsCurrentUserDeviationAuthor(deviation);
    const { restrictToLoggedIn } = useRestrictToLoggedIn();
    const { needsSubscription, isSubscribedButBlocked } = useSubscriptionTier(deviation);
    const owner = getDeviationOwner(deviation);
    const { isWatching, text } = useWatchButton(deviation.author);
    const { isWatching: isWatchingOwner, text: ownerWatchText } = useWatchButton(owner);
    const blockType = useDeviationBlockType(deviation);
    const is_booster = useFlag('booster');
    const { isCoreBadgeAwardable } = useDeviationAttributes(deviation, isAuthor);
    const showSmallerActions = !!height && height < 150;
    const resourceType = getDeviationResourceType(deviation);
    const isMlt = resourceType === DeviationResourceType.ART && !getIsPost(deviation) && (!blockType || blockType === 'matureFilter') && showMlt;
    const biContext = useContext(BiContext);
    const sectionname = biContext.sectionname;
    const dispatch = useDispatch();
    const handleAwardBadgeClick = useCallback(()=>{
        dispatch(beginDeviationAwardBadgeFlow(deviation, AwardBadgeSource.THUMB_OVERLAY, sectionname));
    }, [
        deviation,
        dispatch,
        sectionname
    ]);
    const canShowTitle = useMemo(()=>{
        if (!showTitle) {
            return false;
        }
        switch(blockType){
            case 'subsOnly':
            case 'subBlocked':
                return height && height > 250;
            case 'premiumFolder':
                return height && height > 210;
            case 'watchersOnly':
                return height && height > 180;
            default:
                return true;
        }
    }, [
        blockType,
        height,
        showTitle
    ]);
    const canAwardBadge = !isAuthor && isCoreBadgeAwardable && !(needsSubscription || isSubscribedButBlocked) && showAwardBadge;
    var _deviation_author_useridUuid;
    return /*#__PURE__*/ React.createElement(ThemeOverride, {
        theme: ThemeOptions.DARK,
        className: classname(s['root'], className),
        style: {
            width,
            height
        },
        tag: "div"
    }, /*#__PURE__*/ React.createElement("div", {
        className: s['top']
    }, /*#__PURE__*/ React.createElement("div", {
        className: s['left']
    }), /*#__PURE__*/ React.createElement(HoverOnly, {
        className: s['right']
    }, showExtraActions && extraActions && /*#__PURE__*/ React.createElement(ThumbActions, {
        className: s['actions'],
        deviation: deviation,
        actions: extraActions
    }, /*#__PURE__*/ React.createElement(IconButtonWithBackground, {
        size: showSmallerActions ? 'small' : 'medium',
        icon: Ellipsis,
        "aria-label": t('deviationview.overlay.a11y.moreActions')
    })), onRemove && /*#__PURE__*/ React.createElement(IconButtonWithBackground, {
        className: s['remove-button'],
        size: "medium",
        "aria-label": t('deviationview.overlay.a11y.remove'),
        icon: CloseIcon16,
        onClick: onRemove,
        biData: BiData({
            evid: 405,
            typeid: getDeviationResourceType(deviation),
            itemid: deviation.deviationId,
            rank: BiContextProvidedValue
        })
    }), isMlt && /*#__PURE__*/ React.createElement(IconButtonWithBackground, {
        className: s['mlt-button'],
        iconClassName: s['mlt-icon'],
        size: "medium",
        icon: MltSearch16,
        "aria-label": t('deviationview.overlay.a11y.mlt'),
        href: Url.moreLikeThisLink(deviation.deviationId),
        biData: BiData({
            evid: 126,
            typeid: getDeviationResourceType(deviation),
            itemid: deviation.deviationId,
            creatorid: (_deviation_author_useridUuid = (_deviation_author = deviation.author) === null || _deviation_author === void 0 ? void 0 : _deviation_author.useridUuid) !== null && _deviation_author_useridUuid !== void 0 ? _deviation_author_useridUuid : '',
            sectionname
        })
    }))), /*#__PURE__*/ React.createElement(HoverOnly, {
        className: s['middle']
    }, children), /*#__PURE__*/ React.createElement("div", {
        className: s['bottom']
    }, /*#__PURE__*/ React.createElement(HoverOnly, {
        className: s['left']
    }, is_booster && deviation.boosted && /*#__PURE__*/ React.createElement("div", {
        className: s['boosted']
    }, /*#__PURE__*/ React.createElement(BoostIcon, {
        size: IconSize.SMALLER,
        disableDefaultColors: true
    }), /*#__PURE__*/ React.createElement("span", {
        className: s['boosted-text']
    }, t('common.boosted'))), canShowTitle && /*#__PURE__*/ React.createElement(DeviationLink, {
        deviation: deviation,
        className: s['title']
    }, /*#__PURE__*/ React.createElement(Heading, {
        className: s['title-heading']
    }, getDeviationTitle(deviation))), showAuthor && deviation.author && /*#__PURE__*/ React.createElement("div", {
        className: s['author']
    }, /*#__PURE__*/ React.createElement("div", {
        className: s['avatar-and-username']
    }, /*#__PURE__*/ React.createElement(UserAvatar, {
        user: deviation.author,
        size: 24,
        withLink: true,
        withTooltip: showAuthorTooltip,
        className: s['avatar']
    }), showAuthorName && /*#__PURE__*/ React.createElement(UserLink, {
        user: deviation.author,
        withTooltip: showAuthorTooltip,
        className: s['userlink']
    })), isWatching && showAuthorWatchButton && /*#__PURE__*/ React.createElement(TextButton, {
        size: "small",
        variant: "main",
        disabled: true,
        icon: Check
    }, text)), showOwner && owner && /*#__PURE__*/ React.createElement("div", {
        className: s['author']
    }, /*#__PURE__*/ React.createElement("div", {
        className: s['avatar-and-username']
    }, /*#__PURE__*/ React.createElement(UserAvatar, {
        user: owner,
        size: 24,
        withLink: true,
        withTooltip: showOwnerTooltip,
        className: s['avatar']
    }), showOwnerName && /*#__PURE__*/ React.createElement(UserLink, {
        user: owner,
        withTooltip: showOwnerTooltip,
        className: s['userlink']
    }, /*#__PURE__*/ React.createElement(BasicUserName, {
        user: owner,
        withOwnerText: true
    }))), isWatchingOwner && showOwnerWatchButton && /*#__PURE__*/ React.createElement(TextButton, {
        size: "small",
        variant: "main",
        disabled: true,
        icon: Check
    }, ownerWatchText))), /*#__PURE__*/ React.createElement(HoverOnly, {
        className: classname(s['right'], s['stats'])
    }, canAwardBadge && /*#__PURE__*/ React.createElement(BiButton, {
        className: classname(s['stat'], s['award-button']),
        onClick: restrictToLoggedIn(ReferralType.AWARD_BADGE, handleAwardBadgeClick),
        biData: BiData({
            evid: 320,
            sectionname: BiContextProvidedValue,
            typeid: getDeviationResourceType(deviation),
            itemid: deviation.deviationId
        })
    }, /*#__PURE__*/ React.createElement(GiveBadge, {
        className: s['award-icon'],
        disableDefaultColors: true,
        size: IconSize.SMALLER
    })), showCommentControl && /*#__PURE__*/ React.createElement(CommentControl, {
        deviation: deviation,
        className: s['stat']
    }), showFaveControl && /*#__PURE__*/ React.createElement(FaveControl, {
        deviation: deviation,
        showStatNumbers: true,
        className: s['stat']
    }))));
};
ThumbOverlayHover.displayName = 'ThumbOverlayHover';
export default ThumbOverlayHover;

//# sourceMappingURL=ThumbOverlayHover.js.map